import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SalarySubNav from './SalarySubNav';
import { apiGet, apiSend } from './api';
import { fmtCurrency, fmtDate, periodRowDisplayStatus } from './formatters';
import './SalaryModule.css';
import type { SalaryPeriod } from './types';

const CURRENT_YEAR = new Date().getFullYear();

const PeriodList: React.FC = () => {
  const { sessionToken } = useAuth();
  const [periods, setPeriods] = useState<SalaryPeriod[]>([]);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [generating, setGenerating] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await apiGet<{ periods: SalaryPeriod[] }>('/periods', sessionToken);
      setPeriods(res.periods);
    } catch (e: any) {
      setErr(e.message);
    }
  }, [sessionToken]);

  useEffect(() => { reload(); }, [reload]);

  const generateYear = async () => {
    setGenerating(true);
    setErr('');
    setMsg('');
    try {
      await apiSend('/periods/generate-year', 'POST', { year: selectedYear }, sessionToken);
      setMsg(`Pay periods for ${selectedYear} generated successfully.`);
      reload();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const filtered = periods.filter((p) => new Date(p.start_date).getFullYear() === selectedYear);
  const now = new Date().toISOString().slice(0, 10);
  const currentPeriod = filtered.find((p) => p.start_date <= now && p.end_date >= now);

  return (
    <div className="salary-module">
      <h2>Pay Periods</h2>
      <SalarySubNav />

      {err && <div className="error-banner">{err}</div>}
      {msg && <div className="error-banner" style={{ background: 'var(--cs-ok-soft)', color: 'var(--cs-ok)' }}>{msg}</div>}

      <div className="salary-actions">
        <label style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--cs-ink-2)' }}>Year</label>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          style={{ padding: '0.5rem 0.7rem', borderRadius: 10, border: '1px solid var(--cs-line)', fontSize: '0.9rem', background: 'var(--cs-surface)' }}
        >
          {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <span style={{ color: 'var(--cs-ink-2)', fontSize: '0.82rem' }}>
          {filtered.length} periods · Bi-weekly
        </span>
        {filtered.length === 0 && (
          <button className="salary-btn" onClick={generateYear} disabled={generating}>
            {generating ? 'Generating…' : `Generate ${selectedYear} periods`}
          </button>
        )}
      </div>

      {[...filtered].sort((a, b) => (a.id === currentPeriod?.id ? -1 : b.id === currentPeriod?.id ? 1 : b.start_date.localeCompare(a.start_date))).map((p) => {
        const isCurrent = currentPeriod?.id === p.id;
        const status = periodRowDisplayStatus(p);
        const empCount = p.entry_count || 0;
        return (
          <Link to={`/salary/periods/${p.id}`} className={`sal-row${isCurrent ? ' current' : ''}`} key={p.id}>
            <div className="body">
              <div className="top">
                <b>{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</b>
                <span className={`sal-chip sc-${status}`}>{status}</span>
                {isCurrent && <span className="sal-chip sc-current">Current</span>}
              </div>
              <div className="m">
                Payout {fmtDate(p.payout_date)} · {empCount} {empCount === 1 ? 'employee' : 'employees'}
                {p.unpaid_employee_names && (
                  <span className="unpaid-employees"> · Unpaid: {p.unpaid_employee_names.join(', ') || 'None'}</span>
                )}
              </div>
            </div>
            <div className="right">
              <div className="amt num">{p.total_net != null ? fmtCurrency(p.total_net) : '—'}</div>
              <div className="cap">net</div>
            </div>
            <span className="chev" aria-hidden>›</span>
          </Link>
        );
      })}

      {filtered.length === 0 && (
        <p style={{ textAlign: 'center', color: 'var(--cs-ink-2)', padding: '1.5rem' }}>
          No periods for {selectedYear}. Use “Generate” above to create them.
        </p>
      )}
    </div>
  );
};

export default PeriodList;
